import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  linkedSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';
import { catchError, defer, EMPTY, of, retry, RetryConfig, switchMap, tap } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { AdminModeService } from '../../../auth/admin-mode/admin-mode.service';
import { UserService } from '../../../auth/user/user.service';
import { DealerConsentUpdate } from '../../customer/model/customer-information.model';
import { Customer } from '../../customer/model/customer.model';
import { useI18n } from '../../i18n/services/i18n.service';
import { deepEqual } from '../../utils/objectComparison';
import { ConsentMapper } from '../mapper/consent.mapper';
import { ConsentSelection } from '../model/consent-checkbox';
import {
  NSC_CONSENTS_LAST_SAVED_DATE,
  NSC_CONSENTS_LAST_SAVED_LANGUAGE,
  NSCConsentSelection,
} from '../model/nsc-consent';
import { ConsentProvider } from '../services/consent.provider';
import { ConsentService } from '../services/consent.service';
import { DealerConsentVisibilityService } from '../services/dealer-consent-visibility.service';
import { DealerConsentService } from '../services/dealer-consent.service';
import { GcdmLanguageProvider } from '../services/gcdm-language.provider';

/**
 * Provides a composable which encapsules the logic for consent.
 */
@Injectable({
  // not providedIn: 'root', so that the consents cannot be shared between different customers
  providedIn: null,
})
export class ConsentComposable {
  private readonly consentProvider = inject(ConsentProvider);
  private readonly dealerConsentService = inject(DealerConsentService);
  private readonly dealerConsentVisibilityService = inject(DealerConsentVisibilityService);
  private readonly userService = inject(UserService);
  private readonly consentMapper = inject(ConsentMapper);
  private readonly dsToastService = inject(DsToastService);
  private readonly logger = inject(NGXLogger);

  private readonly i18n = useI18n();

  private useDealerConsents(customer: WritableSignal<Customer | undefined>) {
    const adminModeService = inject(AdminModeService);

    const loadedConsents = computed((): DealerConsentUpdate | undefined => {
      const customerValue = customer();
      if (!customerValue) return undefined;
      if (!customerValue.dealerConsent) return this.dealerConsentService.createNewDealerConsent();
      return customerValue.dealerConsent;
    });
    const consentsAreSaving = signal(false);

    const zeroConsentsAvailable = computed(() => !customer()?.dealerConsent);

    /**
     * The working copy of the dealer consents, as currently being edited.
     *
     * This can be edited, and it will not be written back to the customer until `saveDealerConsent()` is called,
     * triggering a backend request.
     *
     * This signal is linked to the customer, so it will automatically be reset when the customer changes.
     */
    const consents = linkedSignal<DealerConsentUpdate | undefined>(() => {
      const loaded = loadedConsents();
      if (!loaded) {
        return undefined;
      }
      return Object.freeze({ ...loaded });
    });

    const consentSelection = computed((): ConsentSelection | undefined => {
      const loadedDealerConsentSelection = consents();
      if (!loadedDealerConsentSelection) {
        return;
      }

      return Object.freeze({ ...loadedDealerConsentSelection.consent }); // shallow copy
    });

    const consentSaveDate = computed(() => {
      const consentDate = consents()?.consentDate;
      const datePattern = /^\d{4}-\d{2}-\d{2}$/;

      if (!consentDate || !datePattern.test(consentDate)) {
        return;
      }

      const [year, month, day] = consentDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    });

    const isCopyCheckboxSelected = computed(() => {
      const sel = consents()?.legalEntities;
      return sel !== undefined && Object.values(sel).filter(Boolean).length > 1;
    });

    const canBeSaved = computed(() => {
      const equal = deepEqual(loadedConsents()?.consent, consents()?.consent);
      return !equal || zeroConsentsAvailable() || isCopyCheckboxSelected();
    });

    const mandatoryLegalEntities = computed(() => {
      const dpNo = adminModeService.activeAdminDpNr() ?? this.userService.userSignal()?.distributionPartnerNumber;
      return dpNo ? [dpNo] : [];
    });

    const updateConsents = (consentSelection: ConsentSelection | undefined) => {
      try {
        consents.update((consents) =>
          this.dealerConsentService.applyDealerConsentSelection(consents, consentSelection),
        );
      } catch (e) {
        this.logger.error('#CDFTF0117 Failed to reset dealer consent', e);
      }
    };

    const updateLegalEntities = (selectedDistributedPartnerNrs: string[] | undefined) => {
      try {
        consents.update((dealerConsents) =>
          this.dealerConsentService.setDealerLegalEntitiesSelection(dealerConsents, selectedDistributedPartnerNrs),
        );
      } catch (e) {
        this.logger.error('#CDFTF0165 Failed to reset dealer legal entities', e);
      }
    };

    const saveConsent = async () => {
      consentsAreSaving.set(true);
      try {
        // Save consents and update consent date and version number
        const customerWithUpdatedConsent = await this.dealerConsentService.saveDealerConsent(customer(), consents());
        customer.set(customerWithUpdatedConsent);

        this.showConsentSaveSuccess();
      } catch (e) {
        this.logger.error('#CDFTF0114 Failed to save dealer consent', e);
        this.showConsentSaveError();
      } finally {
        consentsAreSaving.set(false);
      }
    };

    const showConsent = computed(() => this.dealerConsentVisibilityService.showDealerConsent());
    const legalText = computed(() => this.dealerConsentService.dealerLegalText());

    const consentCheckboxes = computed(() => {
      return this.consentMapper.mapToDealerConsent(consentSelection());
    });

    return Object.freeze({
      consents,
      showConsent,
      consentSelection,
      updateConsents,
      updateLegalEntities,
      mandatoryLegalEntities,
      legalText,
      consentSaveDate,
      canBeSaved,
      consentsAreSaving: consentsAreSaving.asReadonly(),
      zeroConsentsAvailable,
      saveConsent,
      consentCheckboxes,
    });
  }

  private useNscConsents(gcid: Signal<string | undefined>) {
    const consentSelection = signal<ConsentSelection | undefined>(undefined);
    const consentSelectionError = signal<boolean>(false);
    const consentsAreSaving = signal(false);
    const noConsentAvailable = signal(false);

    const consentSaveDate = signal<Date | undefined>(undefined);

    const nscConsents = computed(() => {
      if (noConsentAvailable()) {
        // Assumption: The selection cannot be retrieved because there are no consents available for this customer.
        // Implicit consents should not be displayed at this stage.
        return this.consentProvider.nonImplicitNscConsents();
      }
      return this.consentProvider.allNscConsents();
    });

    const consentCheckboxes = computed(() => {
      const consents = nscConsents();
      if (!consents) {
        return undefined;
      }
      return this.consentMapper.mapToConsentCheckboxGroups(consents);
    });

    // Computes if every selected consent is of type "IMPLICIT"
    const onlyImplicitConsentsSelected = computed(() => {
      const selection = consentSelection();
      const consentCheckboxes = this.consentProvider.allNscConsents();

      if (
        !selection ||
        Object.values(selection).every((selection) => !selection) ||
        !consentCheckboxes ||
        consentCheckboxes.length === 0
      ) {
        return false;
      }

      return !ConsentService.anyNonImplicitConsentsSelected(selection, consentCheckboxes);
    });

    const gcdmLanguageProvider = inject(GcdmLanguageProvider);

    const loadedNscConsentSelection = signal<ConsentSelection | undefined>(undefined);

    const retryConfig = inject(consentRetryConfigInjectionToken);

    toObservable(gcid)
      .pipe(
        tap(() => {
          noConsentAvailable.set(false);
          consentSelectionError.set(false);
        }),
        switchMap((gcid) =>
          gcid
            ? defer(() => this.consentProvider.loadConsentSelection(gcid)).pipe(
                startWith(undefined),
                retry(retryConfig),
                catchError(() => {
                  // in this case we have no stored consents, or we get another error response code like the user is unknown for GCDM
                  noConsentAvailable.set(true);
                  consentSelectionError.set(true);
                  return EMPTY;
                }),
              )
            : // in case we have no gcid, we display the consents disabled with *empty* selection
              of<NSCConsentSelection>({}),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((consents) => {
        loadedNscConsentSelection.set(consents);
        consentSelection.set(consents);
        consentSaveDate.set(consents?.[NSC_CONSENTS_LAST_SAVED_DATE]);
        gcdmLanguageProvider.lastSavedConsentLanguage.set(consents?.[NSC_CONSENTS_LAST_SAVED_LANGUAGE]);

        if (consents != undefined && Object.keys(consents).length === 0) {
          // No consent saved for the customer
          noConsentAvailable.set(true);
        }
      });

    const canBeSaved = computed(() => {
      const equal = deepEqual(
        ConsentProvider.chosenUsages(loadedNscConsentSelection()),
        ConsentProvider.chosenUsages(consentSelection()),
      );
      return !equal || noConsentAvailable();
    });

    const saveConsent = async () => {
      this.dsToastService.popToast(this.toastId);
      consentsAreSaving.set(true);

      const gcidValue = gcid();

      if (!gcidValue) {
        this.logger.error('#CDFTF0108 Could not save NSC consent as GCID is undefined.');
        this.showConsentSaveError();
        return;
      }

      const currentConsentSelection = consentSelection();
      if (!currentConsentSelection) {
        this.logger.error('#CDFTF0044 consentSelection should not be falsy', {
          consentSelection: currentConsentSelection,
        });
        this.showConsentSaveError();
        return;
      }

      let addImplicits = false;
      if (noConsentAvailable() && Object.entries(currentConsentSelection).length > 0) {
        addImplicits = true;
      }

      try {
        await this.consentProvider.confirmConsentSelection(gcidValue, currentConsentSelection, addImplicits);

        // Has to be to "false" before setting implicit consents locally, as "consentCheckboxes()" does only return non-implicit consents if "noNscConsentsAvailable"=true
        noConsentAvailable.set(false);

        // Implicit consents are automatically set before saving to backend
        // These have to be reflected in the local state.
        if (addImplicits) {
          consentSelection.set(ConsentService.selectImplicitConsents(consentSelection(), nscConsents()));
        }
        loadedNscConsentSelection.set(consentSelection());
        consentSaveDate.set(new Date());
        this.showConsentSaveSuccess();
      } catch {
        this.showConsentSaveError();
      } finally {
        consentsAreSaving.set(false);
      }
    };

    const legalText = computed(() => this.consentProvider.nscLegalText());

    return Object.freeze({
      consentSelection,
      consentSelectionError: consentSelectionError.asReadonly(),
      consentCheckboxes,
      legalText,
      canBeSaved,
      consentSaveDate: consentSaveDate.asReadonly(),
      consentsAreSaving: consentsAreSaving.asReadonly(),
      noConsentAvailable: noConsentAvailable.asReadonly(),
      onlyImplicitConsentsSelected,
      saveConsent,
    });
  }

  /**
   * Composable to manage NSC and Dealer consents for a customer.
   * @param gcid The GCID of the customer.
   * @param customer The customer model signal.
   * @returns An object containing signals and functions to manage consents.
   */
  use(gcid: Signal<string | undefined>, customer: WritableSignal<Customer | undefined>) {
    const nsc = this.useNscConsents(gcid);

    // dealerConsents is not exposed
    const { consents: dealerConsents, ...dealer } = this.useDealerConsents(customer);

    const copyConsent = () => {
      const newDealerConsentsSelection = this.consentMapper.mapToDealerConsentSelection(nsc.consentSelection());

      dealerConsents.update((consents) =>
        this.dealerConsentService.applyDealerConsentSelection(consents, newDealerConsentsSelection),
      );
    };

    return {
      dealer,
      nsc,
      copyConsent,
    };
  }

  private readonly toastId = 'CONSENT_CONTAINER_TOAST';

  private showConsentSaveError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('consent.errorTitle'),
      toastText: this.i18n('consent.saveError'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showConsentSaveSuccess() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('consent.saveSuccessTitle'),
      toastText: this.i18n('consent.saveSuccessMessage'),
      id: this.toastId,
      tone: 'positive',
    });
  }
}

export type UseConsent = ReturnType<ConsentComposable['use']>;

export const consentRetryConfigInjectionToken = new InjectionToken<RetryConfig>('consentRetryConfigInjectionToken', {
  providedIn: 'root',
  factory: () => ({
    count: 5,
    delay: 1000,
  }),
});
